import { useEffect, ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import Grid from '@material-ui/core/Grid';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';

import validate from './validate';
import Input from '~/ui/components/inputs/Input';
import Button from '~/ui/components/common/Button';
import { IAttendingPhysicianCreate } from '~/services/api/attendingPhysicians/types';
import styles from './PhysicianForm.module.scss';
import PhoneFlagInput from '~/ui/components/inputs/PhoneFlag';

interface IProps {
  showForm: boolean;
  initialValues: IAttendingPhysicianCreate;
  isSaving: boolean;
  onAddClick: () => void;
  onCancel: () => void;
  onSubmit: (values: IAttendingPhysicianCreate) => void;
}

const PhysicianForm = ({
  showForm,
  initialValues,
  isSaving,
  onAddClick,
  onCancel,
  onSubmit,
}: IProps): ReactElement => {
  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<IAttendingPhysicianCreate>({
    resolver: validate,
    defaultValues: initialValues,
  });

  useEffect(() => {
    reset(initialValues);
  }, [initialValues]);

  if (!showForm) {
    return (
      <div className={styles.footerButtonContainer}>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          startIcon={<AddCircleOutline />}
          onClick={onAddClick}
        >
          Add More
        </Button>
      </div>
    );
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2} alignItems="flex-end">
        <Grid item className={styles.field}>
          <Input name="name" register={register} errors={errors} label="Name *" />
        </Grid>
        <Grid item className={styles.field}>
          <Input name="specialty" register={register} errors={errors} label="Specialization" />
        </Grid>
        <Grid item className={styles.field}>
          <PhoneFlagInput
            name="phone"
            control={control}
            errors={errors}
            label="Phone"
            setValue={setValue}
          />
        </Grid>
        <Grid item className={styles.field}>
          <PhoneFlagInput
            name="fax"
            control={control}
            errors={errors}
            label="Fax"
            setValue={setValue}
          />
        </Grid>
        <Grid item xs />
        <Grid item>
          <Button variant="outlined" color="primary" disabled={isSaving} onClick={onCancel}>
            Cancel
          </Button>
        </Grid>
        <Grid item>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            isLoading={isSaving}
            disabled={isSaving}
          >
            Save
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default PhysicianForm;
